import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    useTheme,
} from '@mui/material';

import { useTranslation } from 'react-i18next';
import ContentArea from '../../components/contentArea';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import InputField from '../../components/formItem/inputField';
import { SortableHeaderCell, HeaderCell } from '../../components/table/headerCell';
import { getTableStyles } from '../../components/table/styles';
import { createSortConfig, handleSort, toDisplayString } from '../../utils/base';
import { contentAPI } from '../../actions/imsServer';
import { ContentsIconDefault } from '../../assets/images/contentsIconDefault';
import { CONTENT_TYPES, LOCALE, API_PAGE_SIZE } from '../../constants';
import { Prev, Next } from '../../assets/images';

const ContentsList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const theme = useTheme();

    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sortConfig, setSortConfig] = useState(createSortConfig());
    const [typeFilter, setTypeFilter] = useState('');
    const [nameFilter, setNameFilter] = useState('');
    const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const ITEMS_PER_PAGE = API_PAGE_SIZE;
    const isInitialLoad = useRef(true);

    // 분류 옵션
    const typeOptions = [
        { value: '', label: t('all') },
        ...CONTENT_TYPES.map((type) => ({ value: type.value, label: t(type.label) })),
        { value: 'name', label: t('search_by_name') },
    ];

    // 분류명 매핑
    const getTypeLabel = (type) => {
        const option = CONTENT_TYPES.find(opt => opt.value === type);
        return option ? toDisplayString(t(option.label), option.label) : '-';
    };


    // 서버사이드 페이징, 필터, 정렬을 사용한 데이터 로드
    const fetchData = useCallback(async (page = 1, currentTypeFilter = typeFilter, currentNameFilter = debouncedNameFilter, currentSortConfig = sortConfig, showLoading = true) => {
        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            const offset = (page - 1) * ITEMS_PER_PAGE;
            const params = {
                state: 0,
                offset: offset,
                limit: ITEMS_PER_PAGE,
            };

            // 타입 필터 (이름으로 검색이 아닌 경우만)
            if (currentTypeFilter !== '' && currentTypeFilter !== 'name') {
                params.type = currentTypeFilter;
            }

            // 이름 필터 (이름으로 검색 선택 시)
            if (currentTypeFilter === 'name' && currentNameFilter.trim() !== '') {
                params.keyword = currentNameFilter.trim();
            }

            // 서버사이드 정렬
            if (currentSortConfig.key) {
                params.sort = currentSortConfig.key;
                params.order = currentSortConfig.direction === 'asc' ? 'asc' : 'desc';
            }

            const response = await contentAPI.getContents(params);
            const contentsList = response?.data?.contents || response?.contents || [];
            const total = response?.data?.total || response?.total || 0;

            const rows = contentsList.map(content => ({
                index: content.index ?? '',
                type: content.type !== undefined ? content.type : 0,
                ko_name: content.ko_name ?? '',
                ja_name: content.ja_name ?? '',
                de_name: content.de_name ?? '',
                en_name: content.en_name ?? '',
                'zh-Hans_name': content['zh-Hans_name'] ?? '',
                'zh-Hant_name': content['zh-Hant_name'] ?? '',
                icon: content.icon || null,
                endurance: content.endurance !== undefined ? content.endurance : '',
                strength: content.strength !== undefined ? content.strength : '',
                balance: content.balance !== undefined ? content.balance : '',
                flexibility: content.flexibility !== undefined ? content.flexibility : '',
                reaction: content.reaction !== undefined ? content.reaction : '',
                cooperation: content.cooperation !== undefined ? content.cooperation : '',
                info: content.info || '',
            }));

            // 데이터를 한 번에 업데이트하여 깜빡임 최소화
            // React의 배치 업데이트를 활용하기 위해 상태 업데이트를 한 번에 처리
            setContents(prevContents => {
                // 정렬/필터 시에는 기존 데이터를 유지하면서 새 데이터로 교체
                return rows;
            });
            setTotalCount(total);
        } catch (err) {
            console.error('Failed to fetch contents:', err);
            setError(t('failed_to_load_content_list'));
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    }, [typeFilter, debouncedNameFilter, sortConfig, t, i18n.language]);

    // 이름 검색어 디바운스: 입력이 멈춘 뒤에만 API에 반영 (키 입력마다 리프레시 방지)
    useEffect(() => {
        if (typeFilter !== 'name') {
            setDebouncedNameFilter('');
            return;
        }
        const timer = setTimeout(() => setDebouncedNameFilter(nameFilter), 400);
        return () => clearTimeout(timer);
    }, [nameFilter, typeFilter]);

    // 타입/정렬/디바운스된 이름 변경 시 데이터 로드 (초기 로드만 로딩 표시, 이후는 테이블 유지)
    useEffect(() => {
        setCurrentPage(1);
        const showLoading = isInitialLoad.current;
        if (isInitialLoad.current) isInitialLoad.current = false;
        fetchData(1, typeFilter, debouncedNameFilter, sortConfig, showLoading);
    }, [fetchData, typeFilter, debouncedNameFilter, sortConfig]);

    const onSort = (key) => {
        const next = handleSort(sortConfig, key);
        setSortConfig(next);
        // 정렬 변경 시 첫 페이지로 이동하고 데이터 다시 로드 (로딩 표시 없이)
        setCurrentPage(1);
        fetchData(1, typeFilter, debouncedNameFilter, next, false);
    };

    // 페이지 변경 핸들러
    const handlePageChange = useCallback((newPage) => {
        if (newPage < 1) return;
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (newPage > maxPage && maxPage > 0) return;

        setCurrentPage(newPage);
        fetchData(newPage, typeFilter, debouncedNameFilter, sortConfig, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 매 렌더 새 참조라 의존성에서 제외
    }, [totalCount, typeFilter, debouncedNameFilter, sortConfig]);

    // 이전 페이지
    const handlePrevPage = useCallback(() => {
        if (currentPage > 1) {
            handlePageChange(currentPage - 1);
        }
    }, [currentPage, handlePageChange]);

    // 다음 페이지
    const handleNextPage = useCallback(() => {
        const maxPage = Math.ceil(totalCount / ITEMS_PER_PAGE);
        if (currentPage < maxPage) {
            handlePageChange(currentPage + 1);
        }
    }, [currentPage, totalCount, handlePageChange]);

    // 검색 실행 함수
    const handleSearch = useCallback(() => {
        setCurrentPage(1);
        fetchData(1, typeFilter, nameFilter, sortConfig, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchData는 매 렌더 새 참조라 의존성에서 제외
    }, [typeFilter, nameFilter, sortConfig]);

    const handleEdit = (row) => {
        navigate('/contents/edit', { state: { content: row } });
    };

    const handleAdd = () => {
        navigate('/contents/edit', { state: null });
    };

    const handleTypeFilterChange = (option) => {
        setTypeFilter(option.value);
        // 이름으로 검색이 아닌 경우 이름 필터 초기화
        if (option.value !== 'name') {
            setNameFilter('');
        }
    };

    return (
        <ContentArea sx={{
            padding: '20px 0px 0px 0px',
            '&::-webkit-scrollbar': {
                width: '8px',
            },
            '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme.palette.figma?.white?.[20] || 'rgba(0, 0, 0, 0.2)',
                borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: theme.palette.figma?.white?.[40] || 'rgba(0, 0, 0, 0.3)',
            },
        }}>
            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{t('loading_content_list')}</Typography>
                </Box>
            )}

            {error && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography>{error}</Typography>
                </Box>
            )}

            {!loading && !error && (
                <Box sx={{ px: 2.5, mb: 3 }}>
                    {/* 필터 */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Dropdown
                            options={typeOptions}
                            value={typeFilter}
                            onChange={handleTypeFilterChange}
                            placeholder={t('all')}
                            sx={{ minWidth: '120px' }}
                        />
                        {/* 이름으로 검색 선택 시에만 입력란 표시 */}
                        {typeFilter === 'name' && (
                            <>
                                <InputField
                                    placeholder={t('search_by_name')}
                                    value={nameFilter}
                                    onChange={(e) => setNameFilter(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleSearch();
                                        }
                                    }}
                                    onBlur={() => {
                                        // 포커스 아웃 시 검색 실행
                                        handleSearch();
                                    }}
                                    showClearIcon={true}
                                    onClear={() => setNameFilter('')}
                                    sx={{ minWidth: '200px' }}
                                />
                            </>
                        )}
                    </Box>

                    <TableContainer component={Paper}>
                        <Table aria-label="contents table">
                            <TableHead>
                                <TableRow>
                                    <SortableHeaderCell
                                        columnKey="index"
                                        label={t('index')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="type"
                                        label={t('category')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    {LOCALE.map((loc) => (
                                        <HeaderCell key={loc.code} label={`${t(loc.name)} ${t('name')}`} align="center" />
                                    ))}
                                    <HeaderCell label={t('icon')} align="center" />
                                    <SortableHeaderCell
                                        columnKey="endurance"
                                        label={t('endurance')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="strength"
                                        label={t('strength')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="balance"
                                        label={t('balance')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="flexibility"
                                        label={t('flexibility')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="reaction"
                                        label={t('agility')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="cooperation"
                                        label={t('cooperation')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <SortableHeaderCell
                                        columnKey="info"
                                        label={t('note')}
                                        sortConfig={sortConfig}
                                        onSort={onSort}
                                        align="center"
                                    />
                                    <HeaderCell label="" align="center" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(contents) && contents.length > 0 ? contents.map((row) => (
                                    <TableRow key={row.index} sx={{
                                        cursor: 'pointer',
                                        ...getTableStyles(theme).tableRowStyle
                                    }}>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.index}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{getTypeLabel(row.type)}</TableCell>
                                        {LOCALE.map((loc) => (
                                            <TableCell key={loc.code}>{row[`${loc.code}_name`] || '-'}</TableCell>
                                        ))}
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            <Box sx={{
                                                width: '40px',
                                                height: '40px',
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                margin: '0 auto',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor: theme.palette.figma?.white?.[10] || 'rgba(255, 255, 255, 0.1)',
                                                border: `1px solid ${theme.palette.figma?.white?.[20] || 'rgba(255, 255, 255, 0.2)'}`,
                                            }}>
                                                {row.icon ? (
                                                    <img
                                                        src={row.icon}
                                                        alt={row.ko_name || row.en_name || row.name || ''}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                        }}
                                                    />
                                                ) : (
                                                    <ContentsIconDefault />
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.endurance !== '' ? row.endurance : '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.strength !== '' ? row.strength : '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.balance !== '' ? row.balance : '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.flexibility !== '' ? row.flexibility : '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.reaction !== '' ? row.reaction : '-'}</TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>{row.cooperation !== '' ? row.cooperation : '-'}</TableCell>
                                        <TableCell>{row.info || '-'}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="normal"
                                                size="small"
                                                onClick={() => handleEdit(row)}
                                            >
                                                {t('edit')}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={16} sx={{ textAlign: 'center', py: 4 }}>
                                            {loading ? t('loading_data') : t('no_content_data')}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* 페이징 컨트롤 */}
                    {totalCount > 0 && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            padding: '20px 0',
                        }}>
                            <Box
                                onClick={handlePrevPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage > 1 ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage > 1 ? 1 : 0.3,
                                }}
                            >
                                <Prev color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                            <Typography sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)',
                                letterSpacing: '-0.48px',
                                whiteSpace: 'nowrap'
                            }}>
                                {currentPage} / {Math.ceil(totalCount / ITEMS_PER_PAGE)}
                            </Typography>
                            <Box
                                onClick={handleNextPage}
                                sx={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 'pointer' : 'default',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: currentPage < Math.ceil(totalCount / ITEMS_PER_PAGE) ? 1 : 0.3,
                                }}
                            >
                                <Next color={theme.palette.figma?.white?.[80] || 'rgba(255, 255, 255, 0.8)'} size={20} />
                            </Box>
                        </Box>
                    )}

                    {/* 추가 버튼 */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button onClick={handleAdd} sx={{ minWidth: '120px' }}>
                            {t('add')}
                        </Button>
                    </Box>
                </Box>
            )}
        </ContentArea>
    );
};

export default ContentsList;
